import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  FileText,
  Layers,
  Link2,
  Radio,
  Save,
  Sparkles,
  Video,
} from 'lucide-react';
import type {
  CreateGroupContentInput,
  GroupContentConfig,
  GroupContentDetail,
  GroupContentMode,
  GroupContentType,
  UpdateGroupContentInput,
} from '@news-cms/shared';
import { GROUP_CONTENT_MAX_COUNT, GROUP_CONTENT_MIN_COUNT } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { checkGroupSlug } from '@/lib/group-contents';
import { ConfigEditorArticleAuto } from './config-editor-article-auto';
import { ConfigEditorArticleManual } from './config-editor-article-manual';
import { ConfigEditorVideoAuto } from './config-editor-video-auto';
import { ConfigEditorVideoManual } from './config-editor-video-manual';
import { ConfigEditorUrlManual } from './config-editor-url-manual';
import { ConfigEditorStreamingManual } from './config-editor-streaming-manual';
import { ConfigEditorCustomizedManual } from './config-editor-customized-manual';

type SubmitInput = CreateGroupContentInput & Partial<UpdateGroupContentInput>;

interface Props {
  mode: 'create' | 'edit';
  initial?: GroupContentDetail;
  onSubmit: (input: SubmitInput) => void | Promise<void>;
  isSubmitting?: boolean;
}

const TYPE_OPTIONS: {
  value: GroupContentType;
  label: string;
  icon: typeof Layers;
  desc: string;
}[] = [
  { value: 'article', label: 'บทความ', icon: FileText, desc: 'โพสต์จากระบบ /posts' },
  { value: 'video', label: 'วิดีโอ', icon: Video, desc: 'YouTube จากระบบ /videos' },
  { value: 'url', label: 'URL', icon: Link2, desc: 'Link card / embed (กรอกเอง)' },
  { value: 'streaming', label: 'Streaming', icon: Radio, desc: 'Live stream URL (กรอกเอง)' },
  {
    value: 'customized',
    label: 'Customized',
    icon: Sparkles,
    desc: 'ผสมข้ามชนิด (บทความ + วิดีโอ + URL + Stream) ต่อ item',
  },
];

function defaultConfigFor(type: GroupContentType, mode: GroupContentMode): GroupContentConfig {
  if (type === 'article' && mode === 'auto')
    return { kind: 'article-auto', categorySlug: null, sort: 'latest' };
  if (type === 'article' && mode === 'manual') return { kind: 'article-manual', items: [] };
  if (type === 'video' && mode === 'auto')
    return { kind: 'video-auto', playlistId: null, sort: 'latest' };
  if (type === 'video' && mode === 'manual') return { kind: 'video-manual', items: [] };
  if (type === 'streaming') return { kind: 'streaming-manual', items: [] };
  if (type === 'customized') return { kind: 'customized-manual', items: [] };
  return { kind: 'url-manual', items: [] };
}

export function GroupContentForm({ mode: formMode, initial, onSubmit, isSubmitting }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(formMode === 'edit');
  const [type, setType] = useState<GroupContentType>(initial?.type ?? 'article');
  const [mode, setMode] = useState<GroupContentMode>(initial?.mode ?? 'auto');
  const [maxCount, setMaxCount] = useState<number>(initial?.maxCount ?? 6);
  const [config, setConfig] = useState<GroupContentConfig>(
    initial?.config ?? defaultConfigFor('article', 'auto'),
  );
  const [slugStatus, setSlugStatus] = useState<{
    state: 'idle' | 'checking' | 'ok' | 'taken' | 'invalid';
  }>({ state: 'idle' });
  const [formError, setFormError] = useState<string | null>(null);

  // url + streaming + customized are all manual-only — no "auto" source
  // (editor-authored or per-item-typed). Flip silently when admin picks
  // any of them while in auto mode.
  useEffect(() => {
    if ((type === 'url' || type === 'streaming' || type === 'customized') && mode !== 'manual') {
      setMode('manual');
    }
  }, [type, mode]);

  // When type+mode changes, reset config to default for the new pair if it
  // doesn't match. Skip on first mount when initial config already matches
  // — the dep array compares the discriminator.
  useEffect(() => {
    const expected = `${type}-${mode}` as const;
    if (config.kind !== expected) {
      setConfig(defaultConfigFor(type, mode));
    }
  }, [type, mode, config.kind]);

  // Slug availability — debounced check when admin types or when slug is
  // auto-derived from name.
  useEffect(() => {
    if (!slug) {
      setSlugStatus({ state: 'idle' });
      return;
    }
    setSlugStatus({ state: 'checking' });
    const handle = setTimeout(() => {
      void checkGroupSlug(slug, initial?.id).then((res) => {
        if (!res.valid) {
          setSlugStatus({ state: 'invalid' });
        } else if (res.available) {
          setSlugStatus({ state: 'ok' });
        } else {
          setSlugStatus({ state: 'taken' });
        }
      });
    }, 250);
    return () => clearTimeout(handle);
  }, [slug, initial?.id]);

  // Auto-fill slug from name when admin hasn't touched it (create mode only).
  useEffect(() => {
    if (formMode !== 'create') return;
    if (slugTouched) return;
    const auto = name
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^฀-๿a-z0-9]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
    setSlug(auto);
  }, [name, formMode, slugTouched]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!name.trim()) {
      setFormError('กรุณาใส่ชื่อ');
      return;
    }
    if (maxCount < GROUP_CONTENT_MIN_COUNT || maxCount > GROUP_CONTENT_MAX_COUNT) {
      setFormError(
        `จำนวน content ต้องอยู่ระหว่าง ${GROUP_CONTENT_MIN_COUNT}–${GROUP_CONTENT_MAX_COUNT}`,
      );
      return;
    }
    if ('items' in config && config.items.length > maxCount) {
      setFormError(`จำนวน items (${config.items.length}) เกิน maxCount (${maxCount})`);
      return;
    }
    if (slugStatus.state === 'taken' || slugStatus.state === 'invalid') {
      setFormError('slug ไม่พร้อมใช้ — แก้ให้ valid และไม่ซ้ำก่อน');
      return;
    }
    if (config.kind === 'url-manual') {
      // Lightweight client-side guard on URLs — server runs full Zod.
      for (const it of config.items) {
        if (!it.title.trim() || !it.url.trim()) {
          setFormError('ทุกรายการ URL ต้องมี title + url');
          return;
        }
      }
    }
    if (config.kind === 'streaming-manual') {
      for (const it of config.items) {
        if (!it.url.trim()) {
          setFormError('ทุกรายการ streaming ต้องมี URL');
          return;
        }
      }
    }
    if (config.kind === 'customized-manual') {
      for (const it of config.items) {
        if (it.itemType === 'url' && (!it.title.trim() || !it.url.trim())) {
          setFormError('ทุกรายการ URL ใน customized ต้องมีหัวข้อ + URL');
          return;
        }
        if (it.itemType === 'streaming' && !it.url.trim()) {
          setFormError('ทุกรายการ streaming ใน customized ต้องมี URL');
          return;
        }
      }
    }

    const payload: SubmitInput = {
      name: name.trim(),
      slug: slug || undefined,
      type,
      mode,
      maxCount,
      config,
    };
    void onSubmit(payload);
  };

  // Memo the type icon for the type-picker row.
  const TypeIcon = useMemo(() => {
    return TYPE_OPTIONS.find((t) => t.value === type)?.icon ?? Layers;
  }, [type]);

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* Section: Basic */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-slate-50">
          <TypeIcon className="h-4 w-4" /> ข้อมูลพื้นฐาน
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="name">ชื่อ Group *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="เช่น News update, วิดีโอแนะนำ"
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="slug">Slug *</Label>
            <div className="relative">
              <Input
                id="slug"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugTouched(true);
                }}
                placeholder="news-update"
                maxLength={80}
                className="pr-24"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                {slugStatus.state === 'checking' && <span className="text-slate-400">เช็ค…</span>}
                {slugStatus.state === 'ok' && (
                  <span className="flex items-center gap-1 text-emerald-600">
                    <Check className="h-3 w-3" /> ใช้ได้
                  </span>
                )}
                {slugStatus.state === 'taken' && <span className="text-red-500">ใช้แล้ว</span>}
                {slugStatus.state === 'invalid' && (
                  <span className="text-red-500">รูปแบบไม่ถูก</span>
                )}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              public site อ้างถึง group ผ่าน slug นี้ — เปลี่ยนยากภายหลัง (slot ที่อ้างถึงจะ break)
            </p>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Type *</Label>
            <div className="grid gap-2 sm:grid-cols-3">
              {TYPE_OPTIONS.map((t) => {
                const Icon = t.icon;
                const active = type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    className={`flex items-start gap-2 rounded-md border p-3 text-left transition ${
                      active
                        ? 'border-slate-900 bg-slate-50 dark:border-slate-50 dark:bg-slate-800'
                        : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                        {t.label}
                      </div>
                      <div className="text-xs text-slate-500">{t.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Mode *</Label>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={type === 'url'}
                onClick={() => setMode('auto')}
                className={`flex-1 rounded-md border px-3 py-2 text-sm transition ${
                  mode === 'auto'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800/50'
                } ${type === 'url' ? 'cursor-not-allowed opacity-40' : ''}`}
              >
                อัตโนมัติ (Auto)
              </button>
              <button
                type="button"
                onClick={() => setMode('manual')}
                className={`flex-1 rounded-md border px-3 py-2 text-sm transition ${
                  mode === 'manual'
                    ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-400 dark:bg-amber-900/30 dark:text-amber-200'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800/50'
                }`}
              >
                เลือกเอง (Manual)
              </button>
            </div>
            {type === 'url' && (
              <p className="text-xs text-amber-600">URL type ใช้ได้เฉพาะ manual</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="maxCount">จำนวนสูงสุด (1-{GROUP_CONTENT_MAX_COUNT})</Label>
            <Input
              id="maxCount"
              type="number"
              min={GROUP_CONTENT_MIN_COUNT}
              max={GROUP_CONTENT_MAX_COUNT}
              value={maxCount}
              onChange={(e) => setMaxCount(Number(e.target.value))}
            />
          </div>
        </div>
      </section>

      {/* Section: Config (per type+mode) */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-50">
          {mode === 'auto' ? 'กติกาดึง content อัตโนมัติ' : 'รายการ content'}
        </h2>
        {config.kind === 'article-auto' && (
          <ConfigEditorArticleAuto config={config} onChange={setConfig} />
        )}
        {config.kind === 'article-manual' && (
          <ConfigEditorArticleManual config={config} maxCount={maxCount} onChange={setConfig} />
        )}
        {config.kind === 'video-auto' && (
          <ConfigEditorVideoAuto config={config} onChange={setConfig} />
        )}
        {config.kind === 'video-manual' && (
          <ConfigEditorVideoManual config={config} maxCount={maxCount} onChange={setConfig} />
        )}
        {config.kind === 'url-manual' && (
          <ConfigEditorUrlManual config={config} maxCount={maxCount} onChange={setConfig} />
        )}
        {config.kind === 'streaming-manual' && (
          <ConfigEditorStreamingManual config={config} maxCount={maxCount} onChange={setConfig} />
        )}
        {config.kind === 'customized-manual' && (
          <ConfigEditorCustomizedManual config={config} maxCount={maxCount} onChange={setConfig} />
        )}
      </section>

      {formError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isSubmitting}>
          <Save className="mr-1 h-4 w-4" />
          {isSubmitting ? 'กำลังบันทึก…' : formMode === 'create' ? 'สร้าง' : 'บันทึก'}
        </Button>
      </div>
    </form>
  );
}
